import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SidebarContext } from "../../context/SidebarContext.js";
import SectionMenuLeft from "../../components/SectionMenuLeft/SectionMenuLeft.jsx";
import "./Fare.css";
import { toast, ToastContainer } from "react-toastify";
import { config } from "../../config.js";
import ReactTable from "../../components/ReactTable/ReactTable.jsx";
import CustomSelect from "../../components/CustomSelect/CustomSelect.jsx";
import { LuPencilLine } from "react-icons/lu";
import EditFareModal from "../../components/Modals/EditFareModal.jsx";
import BulkFareModal from "../../components/Modals/BulkFareModal.jsx";
import KmFareModal from "../../components/Modals/KmFareModal.jsx";
import { useParams, useNavigate } from "react-router-dom";
import AdminCommissionModal from "../../components/Modals/AdminCommissionModal.jsx";
import { LuArrowLeft } from "react-icons/lu";
import PerKmToggle from "./PerKmToggle.jsx";
import DustingRateModal from "../../components/Modals/DustingRateModal.jsx";

const toTitleCase = (value) => {
  const labels = {
    incity: "Incity",
    minioutstation: "Mini Outstation",
    outstation: "Outstation",
    oneway: "One Way",
    roundtrip: "Round Trip",
    hatchback: "Hatchback",
    sedan: "Sedan",
    suv: "SUV",
    luxury: "Luxury",
    manual: "Manual",
    auto: "Auto",
    ev: "EV",
  };
  const text = String(value || "");
  return (
    labels[text.toLowerCase()] ||
    text.replace(/\b\w/g, (letter) => letter.toUpperCase())
  );
};

const Fare = () => {
  const navigate = useNavigate();
  const { isSidebar } = useContext(SidebarContext);
  const [isCityFetching, setIsCityFetching] = useState(false);
  const [cityList, setCityList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showKmModal, setShowKmModal] = useState(false);
  const [showDustingModal, setShowDustingModal] = useState(false);
  const [dustingRates, setDustingRates] = useState({
    hatchback: 0,
    sedan: 0,
    suv: 0,
    luxury: 0,
  });
  const [isDustingLoading, setIsDustingLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fareList, setFareList] = useState([]);
  const [isFareFetching, setIsFareFetching] = useState(false);
  const [editFare, setEditFare] = useState(null);
  const [isOneWayLoading, setIsOneWayLoading] = useState(false);
  const [perKmFareStatus, setPerKmFareStatus] = useState(false);
  const role = localStorage.getItem("adminRole");
  const adminCity =
    role === "sub-admin" ? localStorage.getItem("adminCity") : null;
  const tripSections = ["incity", "minioutstation", "outstation"];
  const gearTypes = ["manual", "auto", "ev"];
  const vehicleTypes = ["hatchback", "sedan", "suv", "luxury"];
  const { city } = useParams();
  const [adminCommission, setAdminCommission] = useState(0);
  const [showAdminCommissionModal, setShowAdminCommissionModal] =
    useState(false);
  const [isAdminCommissionLoading, setIsAdminCommissionLoading] =
    useState(false);
  const [durationSettings, setDurationSettings] = useState({
    maxDuration: {},
    minDuration: {},
  });
  const [maxDurationInput, setMaxDurationInput] = useState("");
  const [minDurationInput, setMinDurationInput] = useState("");
  const [isMaxDurationLoading, setIsMaxDurationLoading] = useState(false);
  const [isMinDurationLoading, setIsMinDurationLoading] = useState(false);
  const [pendingMaxDuration, setPendingMaxDuration] = useState(null);

  const fetchAdminCommission = useCallback(async () => {
    try {
      const response = await fetch(
        `${config.backEndBaseUrl}referrals/commission`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch admin commission.");
      }
      setAdminCommission(data.bookingCommission || 0);
    } catch (err) {
      toast.error(err.message || "Something went wrong.");
    }
  }, []);

  useEffect(() => {
    fetchAdminCommission();
  }, [fetchAdminCommission]);

  const handleSaveAdminCommission = async () => {
    setIsAdminCommissionLoading(true);
    try {
      const response = await fetch(
        `${config.backEndBaseUrl}referrals/booking-commission`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
          body: JSON.stringify({
            adminBookingCommission: Number(adminCommission),
          }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to update admin commission.");
      }
      toast.success("Admin Commission Updated successfully.");
      setShowAdminCommissionModal(false);
      fetchAdminCommission();
    } catch (err) {
      toast.error(err.message || "Something went wrong.");
    } finally {
      setIsAdminCommissionLoading(false);
    }
  };

  const [filters, setFilters] = useState({
    tripSection: "incity",
    city: city,
    vehicleType: "",
    gearType: "",
  });

  const fetchDurations = useCallback(async () => {
    try {
      const response = await fetch(
        `${config.backEndBaseUrl}fare/durations`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Failed to fetch duration settings.");
      setDurationSettings(data.data || {});
    } catch (error) {
      toast.error(error.message || "Failed to fetch duration settings.");
    }
  }, []);

  const requestMaxDurationChange = () => {
    const duration = Number(maxDurationInput);

    if (maxDurationInput === "") return;

    if (!Number.isFinite(duration) || duration < 0) {
      toast.error("Max duration must be a non-negative number.");
      return;
    }

    const savedConfig = durationSettings.maxDuration?.[filters.tripSection];

    const savedDuration = savedConfig?.duration ?? null;
    const unit = savedConfig?.unit ?? maxDurationUnit;

    if (savedDuration !== null && Number(savedDuration) === duration) {
      return;
    }

    setPendingMaxDuration({
      tripSection: filters.tripSection,
      duration,
      unit,
      previousValue: savedDuration,
    });

    toast.warn("This change will affect future bookings.");
  };

  const handleSaveMaxDuration = async () => {
    if (!pendingMaxDuration) return;
    setIsMaxDurationLoading(true);
    try {
      const response = await fetch(
        `${config.backEndBaseUrl}fare/durations`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
          body: JSON.stringify({
            type: "maxDuration",
            tripSection: pendingMaxDuration.tripSection,
            duration: pendingMaxDuration.duration,
            unit: pendingMaxDuration.unit,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Failed to update max duration.");
      setDurationSettings(data.data || {});
      setPendingMaxDuration(null);
      toast.success("Max duration updated successfully.");
    } catch (error) {
      toast.error(error.message || "Failed to update max duration.");
    } finally {
      setIsMaxDurationLoading(false);
    }
  };

  const cancelMaxDurationChange = () => {
    if (pendingMaxDuration?.tripSection === filters.tripSection) {
      const previous = pendingMaxDuration.previousValue;

      setMaxDurationInput(
        previous === null || previous === undefined ? "" : String(previous),
      );
    }
    setPendingMaxDuration(null);
  };

  const handleSaveMinDuration = async () => {
    if (minDurationInput === "") return;
    const duration = Number(minDurationInput);
    if (!Number.isFinite(duration) || duration < 0) {
      toast.error("Min duration must be a non-negative number.");
      return;
    }
    const savedDuration = durationSettings.minDuration?.[filters.tripSection]?.duration;
    if (savedDuration !== undefined && Number(savedDuration) === duration) return;

    setIsMinDurationLoading(true);
    try {
      const response = await fetch(`${config.backEndBaseUrl}fare/durations`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        body: JSON.stringify({
          type: "minDuration",
          tripSection: filters.tripSection,
          duration,
          unit: maxDurationUnit,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to update min duration.");
      setDurationSettings(data.data || {});
      toast.success("Min duration updated successfully.");
    } catch (error) {
      toast.error(error.message || "Failed to update min duration.");
      const previous = durationSettings.minDuration?.[filters.tripSection]?.duration;
      setMinDurationInput(previous === undefined ? "" : String(previous));
    } finally {
      setIsMinDurationLoading(false);
    }
  };

  useEffect(() => {
    fetchDurations();
  }, [fetchDurations]);

  useEffect(() => {
    const maxConfig = durationSettings.maxDuration?.[filters.tripSection];
    const minConfig = durationSettings.minDuration?.[filters.tripSection];

    setMaxDurationInput(
      maxConfig?.duration === null || maxConfig?.duration === undefined ? "" : String(maxConfig.duration),
    );
    setMinDurationInput(
      minConfig?.duration === null || minConfig?.duration === undefined ? "" : String(minConfig.duration),
    );
  }, [filters.tripSection, durationSettings]);

  const maxDurationUnit =
    durationSettings.maxDuration?.[filters.tripSection]?.unit ||
    (filters.tripSection === "outstation" ? "days" : "hrs");
  const openEditModal = (fare) => {
    setEditFare(fare);
  };

  const getInitialFormData = (city) => ({
    country: "India",
    state: "west bengal",
    city: city,
    vehicleType: "",
    tripSection: "",
    gearType: "",
    fares: {
      baseCharge: "",
      perMinuteCharge: "",
      perHalfHourCharge: "",
      perHourCharge: "",
      per12HoursCharge: "",
    },
  });

  const [formData, setFormData] = useState(getInitialFormData(city));

  const [oneWayFare, setOneWayFare] = useState({
    incity: 0,
    outstation: 0,
    minioutstation: 0,
  });

  const fetchOneWayFare = useCallback(async () => {
    try {
      const oneWayFareUrl = `${config.backEndBaseUrl}fare/one-way-fares?city=${encodeURIComponent(
        city,
      )}`;
      const response = await fetch(oneWayFareUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch cities: ${response.status}`);
      }

      const data = await response.json();
      setOneWayFare(data.data || null);
    } catch (error) {
      toast.error("Failed to fetch cities");
    }
  }, [city]);

  const fetchCities = useCallback(async () => {
    setIsCityFetching(true);
    try {
      const response = await fetch(`${config.backEndBaseUrl}city/get`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch cities: ${response.status}`);
      }

      const data = await response.json();
      setCityList(data.cities || []);
    } catch (error) {
      toast.error("Failed to fetch cities");
    } finally {
      setIsCityFetching(false);
    }
  }, []);

  const handleCloseAddModal = () => {
    setFormData(getInitialFormData(city));
    setShowModal(false);
  };

  useEffect(() => {
    fetchOneWayFare();
  }, [fetchOneWayFare]);

  const fetchDustingRates = useCallback(async () => {
    try {
      const response = await fetch(
        `${config.backEndBaseUrl}fare/dusting-rates?city=${encodeURIComponent(city)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Failed to fetch dusting rates.");
      setDustingRates(data.data);
    } catch (error) {
      toast.error(error.message || "Failed to fetch dusting rates.");
    }
  }, [city]);

  useEffect(() => {
    fetchDustingRates();
  }, [fetchDustingRates]);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  const handleFareChange = (e) => {
    const { name, value } = e.target;
    const numericValue = Number(value);

    setFormData((prev) => {
      const fares = { ...prev.fares };
      const isMini = prev.tripSection === "minioutstation";

      if (isMini) {
        if (name === "per12HoursCharge") {
          const perHour = Number((numericValue / 12).toFixed(2));

          fares.per12HoursCharge = numericValue;
          fares.perHourCharge = perHour;
        } else if (name === "perHourCharge") {
          const per12 = Number((numericValue * 12).toFixed(2));

          fares.perHourCharge = numericValue;
          fares.per12HoursCharge = per12;
        } else {
          fares[name] = numericValue;
        }
      } else {
        if (name === "perMinuteCharge") {
          fares.perMinuteCharge = numericValue;
          fares.perHalfHourCharge = numericValue * 30;
          fares.perHourCharge = numericValue * 60;
        } else if (name === "perHalfHourCharge") {
          const perMinute = numericValue / 30;

          fares.perHalfHourCharge = numericValue;
          fares.perMinuteCharge = perMinute;
          fares.perHourCharge = perMinute * 60;
        } else if (name === "perHourCharge") {
          const perMinute = numericValue / 60;

          fares.perHourCharge = numericValue;
          fares.perMinuteCharge = perMinute;
          fares.perHalfHourCharge = perMinute * 30;
        } else {
          fares[name] = numericValue;
        }
      }

      return {
        ...prev,
        fares,
      };
    });
  };

  const handleSaveFare = async () => {
    if (
      !formData.tripSection ||
      !formData.city ||
      !formData.vehicleType ||
      !formData.gearType
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    // Validate numeric fields
    const { tripSection, fares } = formData;

    if (tripSection === "minioutstation") {
      if (!fares.baseCharge || !fares.per12HoursCharge) {
        toast.error("Please fill all required fields for mini outstation.");
        return;
      }
    } else {
      if (
        !fares.baseCharge ||
        !fares.perMinuteCharge ||
        !fares.perHalfHourCharge ||
        !fares.perHourCharge
      ) {
        toast.error("Please fill all required fields.");
        return;
      }
    }

    try {
      setIsSaving(true);
      const isMini = formData.tripSection === "minioutstation";
      const response = await fetch(`${config.backEndBaseUrl}fare/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },

        body: JSON.stringify({
          ...formData,
          fares: {
            ...formData.fares,
            baseCharge: Number(formData.fares.baseCharge),
            // adminCommission: Number(formData.fares.adminCommission),
            // perKmFare: Number(formData.fares.perKmFare),
            perMeterFare: Number(formData.fares.perMeterFare),
            ...(isMini
              ? {
                  per12HoursCharge: Number(formData.fares.per12HoursCharge),
                }
              : {
                  perMinuteCharge: Number(formData.fares.perMinuteCharge),
                  perHalfHourCharge: Number(formData.fares.perHalfHourCharge),
                  perHourCharge: Number(formData.fares.perHourCharge),
                }),
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create fare.");
      }

      toast.success("Fare created successfully.");

      // Reset form
      setFormData({
        country: "India",
        state: "west bengal",
        city: city,
        vehicleType: "",
        tripSection: "incity",
        gearType: "",
        fares: {
          baseCharge: "",
          perMinuteCharge: "",
          perHalfHourCharge: "",
          perHourCharge: "",
          adminCommissionType: "fixed",
          adminCommission: "",
          per12HoursCharge: "",
        },
      });
      fetchFares();
      setShowModal(false);
    } catch (error) {
      toast.error(error.message || "Something went wrong.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveOneWayFare = async () => {
    setIsOneWayLoading(true);
    try {
      const oneWayFareUrl = `${config.backEndBaseUrl}fare/one-way-fares?city=${encodeURIComponent(
        city,
      )}`;
      const response = await fetch(oneWayFareUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        body: JSON.stringify(oneWayFare),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to create fare.");
      }

      toast.success("Fares Updated successfully.");

      // Reset form
      setOneWayFare({
        incity: 0,
        outstation: 0,
        minioutstation: 0,
      });
      fetchOneWayFare();
      fetchFares();
      setShowKmModal(false);
    } catch (err) {
      toast.error(err.message || "Something went wrong.");
    } finally {
      setIsOneWayLoading(false);
    }
  };

  const handleSaveBulkFare = async ({ fares, combinations }) => {
    setIsSaving(true);
    try {
      const response = await fetch(`${config.backEndBaseUrl}fare/bulk-create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        body: JSON.stringify({
          city,
          tripSection: filters.tripSection,
          fares,
          combinations,
        }),
      });
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Unable to create fares.");
      toast.success(data.message);
      setShowModal(false);
      fetchFares();
    } catch (error) {
      toast.error(error.message || "Unable to create fares.");
    } finally {
      setIsSaving(false);
    }
  };

  const openAddModal = () => {
    setFormData({
      ...getInitialFormData(city),
      tripSection: filters.tripSection,
    });
    setShowModal(true);
  };

  const handleSaveDustingRates = async () => {
    setIsDustingLoading(true);
    try {
      const response = await fetch(
        `${config.backEndBaseUrl}fare/dusting-rates?city=${encodeURIComponent(city)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
          body: JSON.stringify(dustingRates),
        },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Failed to update dusting rates.");
      toast.success("Dusting rates updated successfully.");
      setShowDustingModal(false);
      fetchFares();
    } catch (error) {
      toast.error(error.message || "Failed to update dusting rates.");
    } finally {
      setIsDustingLoading(false);
    }
  };

  const fetchFares = useCallback(async () => {
    setIsFareFetching(true);

    try {
      const queryParams = new URLSearchParams();

      if (filters.tripSection)
        queryParams.append("tripSection", filters.tripSection);

      if (filters.city) queryParams.append("city", filters.city.toLowerCase());

      if (filters.vehicleType)
        queryParams.append("vehicleType", filters.vehicleType);

      if (filters.gearType) queryParams.append("gearType", filters.gearType);

      const finalUrl = `${config.backEndBaseUrl}fare?${queryParams.toString()}`;

      // console.log("Final URL:", finalUrl);

      const response = await fetch(finalUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch fares.");
      }

      setFareList(data.fares || []);
    } catch (error) {
      toast.error(error.message || "Failed to fetch fares.");
      setFareList([]);
    } finally {
      setIsFareFetching(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchFares();
  }, [fetchFares]);

  const columns = useMemo(() => {
    const cols = [
      // {
      //   accessorKey: "tripSection",
      //   header: "Trip Section",
      //   Cell: ({ row }) => toTitleCase(row.original.tripSection),
      // },
      // {
      //   accessorKey: "city",
      //   header: "City",
      //   Cell: ({ row }) => toTitleCase(row.original.city),
      // },
      {
        accessorKey: "vehicleType",
        header: "Vehicle Type",
        Cell: ({ row }) => toTitleCase(row.original.vehicleType),
      },
      {
        accessorKey: "gearType",
        header: "Gear Type",
        Cell: ({ row }) => toTitleCase(row.original.gearType),
      },
      {
        header: "Base Charge",
        Cell: ({ row }) => <>₹ {row.original?.fares?.baseCharge}</>,
      },
    ];

    if (filters.tripSection === "outstation") {
      cols.push({
        header: "Per 12 Hours",
        accessorFn: (row) => row.fares?.per12HoursCharge,
      });
    } else {
      cols.push(
        {
          header: "Per Minute",
          accessorFn: (row) => row.fares?.perMinuteCharge,
        },
        {
          header: "Per half hour",
          accessorFn: (row) => row.fares?.perHalfHourCharge,
        },
      );
    }
    cols.push(
      {
        header: "Per hour",
        accessorFn: (row) => row.fares?.perHourCharge,
      },
      {
        header: "Admin Commission",
        accessorFn: (row) => `${row.adminCommission} %`,
      },
      {
        header: "Created At",
        accessorFn: (row) => new Date(row.createdAt).toLocaleDateString(),
      },
      {
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => (
          <button
            className="btn btn-sm btn-success"
            onClick={() => openEditModal(row.original)}
          >
            <LuPencilLine size={18} />
          </button>
        ),
      },
    );

    return cols;
  }, [filters]);

  const TogglePerKm = async (newValue) => {
    //  instant UI update
    setPerKmFareStatus(newValue);

    try {
      const res = await fetch(
        `${config.backEndBaseUrl}fare/perkmcharge/status/${city}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-dashboard-request": "true",
          },
          body: JSON.stringify({ enabled: newValue }),
        },
      );

      if (!res.ok) throw new Error();
    } catch (err) {
      //  rollback if failed
      setPerKmFareStatus((prev) => !prev);
      toast.error("Failed to update");
    }
  };

  const fetchPerKmStatus = useCallback(async () => {
    try {
      const res = await fetch(
        `${config.backEndBaseUrl}fare/perkmcharge/status/${city}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-dashboard-request": "true",
          },
        },
      );

      if (!res.ok) throw new Error();

      const data = await res.json();
      setPerKmFareStatus(data?.data?.enabled ?? false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch per km fare status");
    }
  }, [city]);

  useEffect(() => {
    fetchPerKmStatus();
  }, [fetchPerKmStatus]);

  return (
    <div className="body">
      <div id="wrapper">
        <div id="page">
          <div className="layout-wrap">
            <SectionMenuLeft />
            <div className="section-content-right">
              <div
                className="main-content"
                style={{ paddingLeft: isSidebar ? "280px" : 0 }}
              >
                <div className="main-content-inner">
                  <ToastContainer />

                  <div className="wg-box">
                    {/* Top Header (Back + Title only) */}
                    <div className="flex items-center justify-between mb-12 flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <button
                          className="back-btn flex items-center gap-2"
                          onClick={() => navigate(-1)}
                        >
                          <LuArrowLeft size={16} />
                          {/* <span>Back</span> */}
                        </button>

                        <h5 className="fare-heading">
                          Fare Management{" "}
                          {city?.charAt(0).toUpperCase() + city?.slice(1)}
                        </h5>
                      </div>
                    </div>

                    {/* Tabs + Actions in SAME LINE */}
                    <div className="trip-tabs-wrapper">
                      {/* LEFT → Tabs */}
                      <div className="trip-tabs">
                        {tripSections.map((section) => (
                          <button
                            key={section}
                            className={`trip-tab ${
                              filters.tripSection === section ? "active" : ""
                            }`}
                            onClick={() =>
                              setFilters((prev) => ({
                                ...prev,
                                tripSection: section,
                              }))
                            }
                          >
                            {toTitleCase(section)}
                          </button>
                        ))}
                      </div>

                      {/* RIGHT → Actions */}
                      <div className="trip-actions">
                        <div className="flex items-center gap-2">
                          <label className="flex items-center gap-2 cursor-pointer border border-success p-3 rounded-lg">
                            <PerKmToggle
                              value={perKmFareStatus}
                              onChange={async (newValue) => {
                                await TogglePerKm(newValue);
                              }}
                            />
                            <span className="text-lg">Per Km Rate</span>
                          </label>
                        </div>
                        <button
                          className="btn btn-outline-success"
                          onClick={() => setShowAdminCommissionModal(true)}
                        >
                          Set Admin Commission
                        </button>

                        <button
                          className="btn btn-outline-success"
                          onClick={() => setShowKmModal(true)}
                        >
                          One Way Fare (₹/km)
                        </button>
                        <button
                          className="btn btn-outline-success"
                          onClick={() => setShowDustingModal(true)}
                        >
                          Dusting Rate
                        </button>
                        <div className="max-duration-control">
                          <label htmlFor="min-duration-input">
                            Min duration {maxDurationUnit === "days" ? " day(s)" : " hr(s)"}
                          </label>
                          <input
                            id="min-duration-input"
                            className="max-duration-input duration-input-compact"
                            type="number"
                            min="0"
                            step={filters.tripSection === "outstation" ? "1" : "0.5"}
                            value={minDurationInput}
                            onChange={(event) => setMinDurationInput(event.target.value)}
                            onBlur={handleSaveMinDuration}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") event.currentTarget.blur();
                            }}
                            placeholder="Enter duration"
                            aria-label={`Minimum ${maxDurationUnit} for ${toTitleCase(filters.tripSection)}`}
                            disabled={isMinDurationLoading}
                          />
                        </div>
                        <div className="max-duration-control">
                          <label htmlFor="max-duration-input">
                            Max duration 
                            {maxDurationUnit === "days" ? " day(s)" : " hr(s)"}
                          </label>
                          <input
                            id="max-duration-input"
                            className="max-duration-input"
                            type="number"
                            min="0"
                            step={
                              filters.tripSection === "outstation" ? "1" : "0.5"
                            }
                            value={maxDurationInput}
                            onChange={(event) =>
                              setMaxDurationInput(event.target.value)
                            }
                            onBlur={requestMaxDurationChange}
                            onKeyDown={(event) => {
                              if (event.key === "Enter")
                                event.currentTarget.blur();
                            }}
                            placeholder="Enter duration"
                            aria-label={`Maximum ${maxDurationUnit} for ${toTitleCase(filters.tripSection)}`}
                            disabled={isMaxDurationLoading}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="filter-container">
                      <div className="filter-header">
                        <h6>Filters</h6>
                      </div>
                      <div className="filter-row">
                        {/* <CustomSelect
                                options={tripSections}
                                value={filters.tripSection}
                                placeholder="Filter Trip Section"
                                onChange={(val) =>
                                    setFilters(prev => ({ ...prev, tripSection: val }))
                                }
                            /> */}

                        {/* <CustomSelect
                                options={adminCity ? [adminCity.toLocaleLowerCase()] : cityList?.map(c => c.city).filter(Boolean) || []}
                                value={adminCity ? adminCity.toLocaleLowerCase() : filters.city}
                                placeholder="Filter City"
                                onChange={(val) =>
                                    setFilters(prev => ({ ...prev, city: val }))
                                }
                            /> */}

                        <CustomSelect
                          options={vehicleTypes}
                          value={filters.vehicleType}
                          placeholder="Filter Vehicle"
                          formatLabel={toTitleCase}
                          onChange={(val) =>
                            setFilters((prev) => ({
                              ...prev,
                              vehicleType: val,
                            }))
                          }
                        />

                        <CustomSelect
                          options={gearTypes}
                          value={filters.gearType}
                          placeholder="Filter Gear"
                          formatLabel={toTitleCase}
                          onChange={(val) =>
                            setFilters((prev) => ({ ...prev, gearType: val }))
                          }
                        />

                        <button
                          className="btn btn-dark w-auto"
                          onClick={() => {
                            setFilters({
                              tripSection: "incity",
                              city: city,
                              vehicleType: "",
                              gearType: "",
                            });
                          }}
                        >
                          Reset
                        </button>
                        <button
                          className="btn btn-success w-auto"
                          onClick={openAddModal}
                        >
                          Add Fare +
                        </button>
                      </div>
                    </div>
                    {isFareFetching ? (
                      <div className="skeleton-table">
                        {[...Array(6)].map((_, i) => (
                          <div className="skeleton-row" key={i}>
                            <div className="skeleton skeleton-name"></div>
                            <div className="skeleton skeleton-status"></div>
                            <div className="skeleton skeleton-phone"></div>
                            <div className="skeleton skeleton-email"></div>
                            <div className="skeleton skeleton-status"></div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <ReactTable columns={columns} data={fareList} />
                    )}
                  </div>
                  {showModal && (
                    <BulkFareModal
                      city={city}
                      tripSection={filters.tripSection}
                      loading={isSaving}
                      onClose={handleCloseAddModal}
                      onSave={handleSaveBulkFare}
                    />
                  )}
                  {editFare && (
                    <EditFareModal
                      fare={editFare}
                      onClose={() => setEditFare(null)}
                      onSuccess={() => {
                        setEditFare(null);
                        fetchFares();
                      }}
                    />
                  )}
                  {showKmModal && (
                    <KmFareModal
                      setShowKmModal={setShowKmModal}
                      oneWayFare={oneWayFare}
                      setOneWayFare={setOneWayFare}
                      isOneWayLoading={isOneWayLoading}
                      handleSaveOneWayFare={handleSaveOneWayFare}
                    />
                  )}
                  {showDustingModal && (
                    <DustingRateModal
                      rates={dustingRates}
                      setRates={setDustingRates}
                      loading={isDustingLoading}
                      onClose={() => setShowDustingModal(false)}
                      onSave={handleSaveDustingRates}
                    />
                  )}
                  {showAdminCommissionModal && (
                    <AdminCommissionModal
                      setShowAdminCommissionModal={setShowAdminCommissionModal}
                      adminCommission={adminCommission}
                      setAdminCommission={setAdminCommission}
                      isAdminCommissionLoading={isAdminCommissionLoading}
                      handleSaveAdminCommission={handleSaveAdminCommission}
                    />
                  )}
                  {pendingMaxDuration && (
                    <div
                      className="max-duration-confirm-overlay"
                      role="presentation"
                    >
                      <div
                        className="max-duration-confirm-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="max-duration-confirm-title"
                      >
                        <h5 id="max-duration-confirm-title">
                          Confirm max duration change
                        </h5>
                        <p>
                          Change {toTitleCase(pendingMaxDuration.tripSection)}{" "}
                          max duration to{" "}
                          <strong>
                            {pendingMaxDuration.duration}{" "}
                            {pendingMaxDuration.unit}
                          </strong>
                          ?
                        </p>
                        <p className="max-duration-warning">
                          This will affect future bookings. Existing bookings
                          will not be changed.
                        </p>
                        <div className="max-duration-confirm-actions">
                          <button
                            className="btn btn-light"
                            onClick={cancelMaxDurationChange}
                            disabled={isMaxDurationLoading}
                          >
                            No, keep current
                          </button>
                          <button
                            className="btn btn-success"
                            onClick={handleSaveMaxDuration}
                            disabled={isMaxDurationLoading}
                          >
                            {isMaxDurationLoading
                              ? "Updating..."
                              : "Yes, update"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Fare;
